'use client';

import { useTranslations } from '@power-core/toolkit';
import { type FunctionComponent, useState } from 'react';
import DatePicker from 'react-datepicker';

import { AbbCaretDown } from '@/assets/icons/components/abb-caret-down';
import { useDate } from '@/modules/utils/custom-hooks/use-date';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';

import { Button } from '../button/button';
import { Dropdown } from '../dropdown/dropdown';
import { Flexbox } from '../flexbox/flexbox';
import type { CustomHeaderParams, DatepickerProps } from './types';

import 'react-datepicker/dist/react-datepicker.css';
import './datepicker.css';
import { Checkbox } from '../checkbox/checkbox';

export const Datepicker: FunctionComponent<DatepickerProps> = ({
	setIsOpen,
	onSelect,
	setSearchTerm,
	maxDate = new Date(),
	minDate = new Date(0),
	showPreviousMonths = true,
	showUnlimitedCheckbox = false,
	fixedStartDate,
}) => {
	const t = useTranslations();
	const today = new Date();
	const { dayjs, format } = useDate();
	const { isDarkTheme } = useTheme();
	const range = (start: number, end: number) => Array.from({ length: end - start }, (_v, k) => k + start);
	const yearsRange = range(dayjs(new Date(0)).year(), dayjs(today).year() + 1);
	const [date, setDate] = useState<{ startDate: Date | null; endDate: Date | null }>({
		startDate: fixedStartDate ?? today,
		endDate: null,
	});
	const [isUnlimitedChecked, setUnlimitedChecked] = useState<boolean>(false);

	const onChange = (dates: [Date | null, Date | null] | Date | null) => {
		if (fixedStartDate) {
			const end = Array.isArray(dates) ? dates[1] : dates;
			const endDayJs = new Date(dayjs(end).endOf('day').format());
			setDate({
				startDate: fixedStartDate,
				endDate: String(endDayJs) === 'Invalid Date' ? null : endDayJs,
			});
		} else {
			const [start, end] = dates as [Date | null, Date | null];
			const startDayJs = new Date(dayjs(start).startOf('day').format());
			const endDayJs = new Date(dayjs(end).endOf('day').format());
			setDate({
				startDate: String(startDayJs) === 'Invalid Date' ? null : startDayJs,
				endDate: String(endDayJs) === 'Invalid Date' ? null : endDayJs,
			});
		}
	};

	const handleCancelButton = () => {
		setDate({ startDate: today, endDate: today });
		setIsOpen(false);
	};

	const handleSelectButton = () => {
		if (!date.startDate && !date.endDate) return;

		if (isUnlimitedChecked) {
			onSelect('unlimited');
			setIsOpen(false);
			return;
		}

		const formattedStart = format(dayjs(date.startDate), 'DD/MM/YYYY');
		const formattedEnd = format(dayjs(date.endDate), 'DD/MM/YYYY');

		onSelect([dayjs(date.startDate).toISOString(), dayjs(date.endDate).toISOString()]);
		setSearchTerm?.(`${formattedStart} - ${formattedEnd}`);
		setIsOpen(false);
	};

	const pickerModeProps = !fixedStartDate ? { selectsRange: true as const } : { selectsEnd: true as const };

	const renderCustomHeader = ({ monthDate, decreaseMonth, increaseMonth, customHeaderCount, changeYear }: CustomHeaderParams) => {
		return (
			<div className='flex ml-1 mb-4 bg-white justify-between dark:bg-bg_dark dark:text-n700_text_dark_mode'>
				<div className='flex items-center'>
					<h2
						className={`${customHeaderCount === 0 ? 'visible' : 'hidden'} font-semibold text-sm`}
					>{`${monthDate.toLocaleString('en-US', { month: 'long' })}, ${monthDate.toLocaleString('en-US', { year: 'numeric' })}`}</h2>
					{customHeaderCount !== 0 ? (
						<Dropdown
							className='scrollbar-rounded'
							size={'sm'}
							label={`${monthDate.toLocaleString('en-US', { month: 'long' })}, ${monthDate.toLocaleString('en-US', { year: 'numeric' })}`}
							menuItems={yearsRange.reverse().map((year) => ({
								label: year.toString(),
								value: year.toString(),
								onClick: () => changeYear(year),
							}))}
							isMenu
							isCalendar
							inline
						/>
					) : null}
				</div>
				<div className='flex'>
					<button
						type='button'
						style={{ position: 'relative', width: '15px', height: '15px', marginRight: '10px' }}
						aria-label='Previous Month'
						className={'react-datepicker__navigation react-datepicker__navigation--previous'}
						onClick={decreaseMonth}
					>
						<AbbCaretDown fill={isDarkTheme ? '#f1f1f1' : '#666666'} className={'rotate-90'} />
					</button>
					<button
						type='button'
						style={{ position: 'relative', width: '15px', height: '15px' }}
						aria-label='Next Month'
						className={'react-datepicker__navigation react-datepicker__navigation--next'}
						onClick={increaseMonth}
					>
						<AbbCaretDown fill={isDarkTheme ? '#f1f1f1' : '#666666'} className={'-rotate-90'} />
					</button>
				</div>
			</div>
		);
	};

	return (
		<div className='bg-white w-fit dark:bg-bg_dark'>
			<DatePicker
				selected={fixedStartDate ? date.endDate : date.startDate}
				onChange={onChange}
				startDate={date.startDate ?? undefined}
				endDate={date.endDate ?? undefined}
				monthsShown={2}
				minDate={minDate}
				maxDate={maxDate}
				{...pickerModeProps}
				useWeekdaysShort={true}
				calendarStartDay={1}
				showPopperArrow={false}
				inline
				renderDayContents={(day) => <span>{day}</span>}
				showPreviousMonths={showPreviousMonths}
				renderCustomHeader={renderCustomHeader}
			/>
			<Flexbox justify={showUnlimitedCheckbox ? 'between' : 'end'} items='center'>
				{showUnlimitedCheckbox && (
					<Checkbox
						className='!mt-0 ml-6 mb-6'
						sizing='small'
						color='primary'
						label='Unlimited'
						checked={isUnlimitedChecked}
						onChange={() => setUnlimitedChecked((prev) => !prev)}
					/>
				)}
				<Flexbox className='mr-6 mb-6' justify='end'>
					<Button sizing={'small'} className='w-20 mr-3' variant='ghost-secondary' onClick={handleCancelButton}>
						{t('T_CANCEL')}
					</Button>
					<Button sizing={'small'} className='w-20' onClick={handleSelectButton}>
						{t('T_SELECT')}
					</Button>
				</Flexbox>
			</Flexbox>
		</div>
	);
};
